import type { BackendTypes } from '@tf/api';
import type { ExtendedFormDefinition, FormStatuses } from '@tf/shared';
import { extractForms, getFormHash, getFormStatuses, processReviewGraph } from '@tf/shared';
import { T } from '@tf/utils';
import { useApplication, useApplicationMainReview } from '@/components/application';
import { getReviewConnections } from '@/components/structure/getReviewConnections';
import { useDefinitions } from '@/core/providers';

interface FormInfo {
	form: ExtendedFormDefinition<BackendTypes.FormDefinition>;
	formStatus: FormStatuses;
}

export interface ConnectedEntity {
	entityId: BackendTypes.GraphId;
	entityName: string;
	forms: FormInfo[];
}

export interface Connection {
	isRequired: boolean;
	isUnique: boolean;
	connectionKind: BackendTypes.EntityConnectionKind;
	connectedEntities: ConnectedEntity[];
}

export interface StructureEntity {
	entityId: BackendTypes.GraphId;
	entityKind: BackendTypes.EntityKind;
	entityName: string;
	forms: FormInfo[];
	connections: Connection[];
}

type ConnectionFilterFn = (connection: BackendTypes.EntityConnection) => boolean;

const defaultConnectionFilter: ConnectionFilterFn = () => true;

const filterSegmentsByBlacklist = (
	segmentIdentities: BackendTypes.SegmentIdentity[],
	entityKind: BackendTypes.EntityKind,
	segmentFilters?: Record<string, string[]>,
): BackendTypes.SegmentIdentity[] => {
	if (!segmentFilters?.[entityKind]) {
		return segmentIdentities;
	}

	const blockedSegments = new Set(segmentFilters[entityKind]);

	return segmentIdentities.filter((segment) => !blockedSegments.has(segment.segmentKind));
};

const buildEntityNamesMap = (application: ReturnType<typeof useApplication>): Record<BackendTypes.GraphId, string> => {
	const entityNames: Record<BackendTypes.GraphId, string> = {};
	application.reviews.forEach((review) => {
		processReviewGraph({
			accountId: application.account.graphNodeId,
			review,
			processorFn: ({ parentEntity, childEntity }) => {
				entityNames[parentEntity.graphNodeId] = parentEntity.name;
				entityNames[childEntity.graphNodeId] = childEntity.name;
			},
		});
	});

	return entityNames;
};

const createOrGetRegisteredEntity = (
	entities: StructureEntity[],
	entity: BackendTypes.Entity,
	connectionFilter: ConnectionFilterFn = defaultConnectionFilter,
): StructureEntity => {
	let registeredEntity = entities.find((e) => e.entityId === entity.graphNodeId);

	if (!registeredEntity) {
		registeredEntity = {
			entityId: entity.graphNodeId,
			entityKind: entity.entityKind,
			entityName: entity.name,
			forms: [],
			connections: entity.listConnections.filter(connectionFilter).map((c) => ({
				isRequired: c.isLogicalRequired,
				isUnique: Boolean(c.isUnique),
				connectionKind: c.connectionKind,
				connectedEntities: [],
			})),
		};
		entities.push(registeredEntity);
	}

	return registeredEntity;
};

function processEntityConnections(
	application: ReturnType<typeof useApplication>,
	mainReview: ReturnType<typeof useApplicationMainReview>,
	definitions: ReturnType<typeof useDefinitions>,
	entityNames: Record<BackendTypes.GraphId, string>,
	applicationConnectionKind: BackendTypes.EntityConnectionKind,
): StructureEntity[] {
	const entities: StructureEntity[] = [];

	const segmentFilters =
		definitions.defs.accounts?.[application.account.entityKind]?.connections?.[applicationConnectionKind]
			?.segment_filters;

	// Add initial entity - account for non-main review, and applicant entity for main (applicant) review
	const isMainReview = applicationConnectionKind === mainReview.connectionKind;

	if (!isMainReview) {
		createOrGetRegisteredEntity(entities, application.account, (c) => c.connectionKind === applicationConnectionKind);
	} else {
		const mainEntity = mainReview.listEntities.find((e) => e.graphNodeId === mainReview.graphNodeId);
		T.assertNotNullish(mainEntity, 'main entity not found');
		createOrGetRegisteredEntity(entities, mainEntity);
	}

	const reviews = getReviewConnections(application, applicationConnectionKind);
	reviews.forEach((review) => {
		processReviewGraph({
			accountId: application.account.graphNodeId,
			review,
			processorFn: ({ parentEntity, childEntity, connection, connectionData }) => {
				// Main (applicant) review structure uses applicant entity as root
				if (connection.connectionKind !== mainReview.connectionKind) {
					const registeredParentEntity = createOrGetRegisteredEntity(entities, parentEntity);

					const registeredConnection = registeredParentEntity.connections.find(
						(c) => c.connectionKind === connection.connectionKind,
					);

					const isEntityAdded = registeredConnection?.connectedEntities.find(
						(e) => e.entityId === connectionData.graphNodeId,
					);
					if (!isEntityAdded && registeredConnection) {
						const segmentIdentities = connectionData.listSegments.map((s) => s.SegmentIdentity);
						const shouldApplyFilter = connection.connectionKind === applicationConnectionKind;
						const filteredSegments = shouldApplyFilter
							? filterSegmentsByBlacklist(segmentIdentities, childEntity.entityKind, segmentFilters)
							: segmentIdentities;

						const forms = extractForms({
							formDefs: definitions.defs.forms,
							segmentIdentities: filteredSegments,
						}).map((form) => {
							return {
								form,
								formStatus: getFormStatuses({
									containers: review.listSegmentReviewContainers,
									segmentIdentities: form.segmentIdentities,
								}),
							};
						});

						registeredConnection.connectedEntities.push({
							entityId: connectionData.graphNodeId,
							entityName: entityNames[connectionData.graphNodeId],
							forms,
						});
					}
				}

				// Add forms for entity
				const isMainEntity =
					review.graphLinkId === mainReview.graphLinkId && childEntity.graphNodeId === mainReview.graphNodeId;

				if (!isMainEntity) {
					const registeredChildEntity = createOrGetRegisteredEntity(entities, childEntity);

					const segmentIdentities = [
						...childEntity.listSegments
							.map((s) => s.SegmentIdentity)
							.filter((s) => s.graphId === connectionData.graphNodeId),
						...review.listSegmentReviewContainers
							.filter((s) => s.identity.graphId === connectionData.graphNodeId)
							.map((s) => s.identity),
					];

					const shouldApplyFilter = connection.connectionKind === applicationConnectionKind;
					const filteredSegments = shouldApplyFilter
						? filterSegmentsByBlacklist(segmentIdentities, childEntity.entityKind, segmentFilters)
						: segmentIdentities;

					const entityForms = extractForms({
						formDefs: definitions.defs.forms,
						segmentIdentities: filteredSegments,
					});

					for (const form of entityForms) {
						const isAdded = !!registeredChildEntity.forms.find(
							(f) => f.form.name === form.name && getFormHash(f.form) === getFormHash(form),
						);
						if (!isAdded) {
							registeredChildEntity.forms.push({
								form,
								formStatus: getFormStatuses({
									containers: review.listSegmentReviewContainers,
									segmentIdentities: form.segmentIdentities,
								}),
							});
						}
					}
				}
			},
		});
	});

	return entities;
}

export const useStructureEntitiesMap = (
	applicationConnectionKinds: BackendTypes.EntityConnectionKind[],
): Record<BackendTypes.EntityConnectionKind, StructureEntity[]> => {
	if (!applicationConnectionKinds.length) return {};

	const definitions = useDefinitions();
	const application = useApplication();
	const mainReview = useApplicationMainReview();
	const entityNames = buildEntityNamesMap(application);
	const result: Record<BackendTypes.EntityConnectionKind, StructureEntity[]> = {};

	applicationConnectionKinds.forEach((kind) => {
		result[kind] = processEntityConnections(application, mainReview, definitions, entityNames, kind);
	});

	return result;
};

export const useStructureEntities = (
	applicationConnectionKind: BackendTypes.EntityConnectionKind,
): StructureEntity[] => {
	const definitions = useDefinitions();
	const application = useApplication();
	const mainReview = useApplicationMainReview();
	const entityNames = buildEntityNamesMap(application);
	return processEntityConnections(application, mainReview, definitions, entityNames, applicationConnectionKind);
};
